/**
 * The living circle on the start screen (#1430, Adam, 14 Sep: "use the orb
 * and tooltip for this"; later that day: "the orb should always be there").
 * A session page's circle is one turn's activity; this one is every session
 * of the user's that is alive. It is always drawn: alive while at least one
 * session has a turn in flight, resting otherwise. Its hover names the
 * working sessions with how long each has been at it, then the sessions that
 * are ready (a live container, nothing running) as shortcuts back into them,
 * and a click on either takes the reader there.
 */
import React, { useEffect, useState } from 'react'
import { Box, ButtonBase, Typography } from '@mui/material'
import ActivityBadge from './ActivityBadge.js'
import type { ActivityLog } from '../hooks/useActivityLog.js'

export interface WorkingSession {
  id: string
  title?: string
  /** A turn in flight, or a live container with nothing running. */
  state: 'working' | 'ready'
  /** When the turn began, unix seconds; unknown or not working when absent. */
  startedAt?: number
}

export interface WorkingSessionsBadgeProps {
  sessions: WorkingSession[]
  onOpen: (sessionId: string) => void
  theme?: 'light' | 'dark'
  /** Test seam: the clock's now, unix seconds. */
  now?: () => number
}

/** "1 session working" / "3 sessions working" — the circle's spoken line. */
export function workingSessionsLabel(count: number): string {
  return `${count} session${count === 1 ? '' : 's'} working`
}

/** "1 session ready" / "3 sessions ready" — the resting circle's hover. */
export function readySessionsLabel(count: number): string {
  return `${count} session${count === 1 ? '' : 's'} ready`
}

/** The hover is a shortcut, not the History tab: past this it says where the rest are. */
export const READY_LIST_MAX = 8

const CLOCK_TICK_MS = 1000

function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

function Row({ session, onOpen, now, testId }: { session: WorkingSession; onOpen: (id: string) => void; now: number; testId: string }) {
  const working = session.state === 'working'
  return (
    <ButtonBase
      data-testid={testId}
      onClick={() => onOpen(session.id)}
      sx={{
        display: 'grid', gridTemplateColumns: working ? '36px 1fr' : '1fr', gap: 1, alignItems: 'baseline',
        width: '100%', textAlign: 'left', py: 0.25, px: 0.5, mx: -0.5, borderRadius: 1,
        font: 'inherit', color: 'inherit',
        '&:hover, &:focus-visible': { bgcolor: 'rgba(255,255,255,0.12)' },
      }}
    >
      {working && (
        <Typography component="span" sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
          {session.startedAt != null ? clock(now - session.startedAt) : ''}
        </Typography>
      )}
      <Typography component="span" sx={{ fontSize: 12.5, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {session.title || 'Untitled'}
      </Typography>
    </ButtonBase>
  )
}

const HEADING_SX = { fontSize: 12.5, fontWeight: 600, lineHeight: 1.3, pb: 0.5 } as const

function SessionsList({ working, ready, onOpen, now }: { working: WorkingSession[]; ready: WorkingSession[]; onOpen: (id: string) => void; now: number }) {
  const shown = ready.slice(0, READY_LIST_MAX)
  const more = ready.length - shown.length
  return (
    <Box data-testid="working-sessions-list" sx={{ py: 0.5, minWidth: 260, maxWidth: 340 }}>
      {working.length > 0 && (
        <Box data-testid="working-sessions-group">
          <Typography component="div" sx={HEADING_SX}>{workingSessionsLabel(working.length)}</Typography>
          {working.map(s => <Row key={s.id} session={s} onOpen={onOpen} now={now} testId="working-session-row" />)}
        </Box>
      )}
      {ready.length > 0 && (
        <Box data-testid="ready-sessions-group" sx={{ pt: working.length > 0 ? 1 : 0 }}>
          <Typography component="div" sx={HEADING_SX}>{readySessionsLabel(ready.length)}</Typography>
          {shown.map(s => <Row key={s.id} session={s} onOpen={onOpen} now={now} testId="ready-session-row" />)}
          {more > 0 && (
            <Typography component="div" sx={{ fontSize: 11.5, opacity: 0.8, pt: 0.5 }}>
              and {more} more in History
            </Typography>
          )}
        </Box>
      )}
      {working.length === 0 && ready.length === 0 && (
        <Typography component="div" sx={{ fontSize: 12.5, lineHeight: 1.3 }}>No sessions running</Typography>
      )}
    </Box>
  )
}

export default function WorkingSessionsBadge({ sessions, onOpen, theme, now = () => Date.now() / 1000 }: WorkingSessionsBadgeProps) {
  // The clocks tick while the circle is on screen; the interval is cleared
  // with it. Lifecycle only — nothing else re-runs this.
  const [tick, setTick] = useState(() => now())
  useEffect(() => {
    const t = setInterval(() => setTick(now()), CLOCK_TICK_MS)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const working = sessions.filter(s => s.state === 'working')
  const ready = sessions.filter(s => s.state === 'ready')
  // One line in force keeps the orb alive; with nothing working it rests, as
  // a session page's circle does between turns. The words are the circle's
  // aria-label and status line.
  const activity: ActivityLog = {
    orbState: 'working',
    current: working.length > 0 ? { id: 1, at: 0, label: workingSessionsLabel(working.length), category: 'tool' } : null,
    log: [],
  }
  return (
    <Box data-testid="working-sessions-badge">
      <ActivityBadge activity={activity} theme={theme} tooltip={<SessionsList working={working} ready={ready} onOpen={onOpen} now={tick} />} />
    </Box>
  )
}
